/**
 * Implements software requirements: H1SR-68, H1SR-149, SRS-215
 *
 * @link https://formuslabs.youtrack.cloud/issue/H1SR-68/User-can-authenticate-with-their-email-as-a-username-and-their-chosen-password
 * @link https://formuslabs.youtrack.cloud/issue/H1SR-149/Keep-user-passwords-secure
 */

import axios, {
    type AxiosAdapter,
    type AxiosError,
    type AxiosResponse,
    HttpStatusCode,
    type InternalAxiosRequestConfig,
} from 'axios';
import { apiBaseUrl } from '@/util/headMetaTags';
import { cachedAuthTokens, unexpiredAuthTokens } from '@/api/cache';
import { refreshTokens } from '@/api/authenticator';
import { useAuth } from '@/stores/auth';
import {
    StaleCaseError,
    caseIdFromMutationUrl,
    currentCaseVersion,
    notifyCaseStale,
    observeCaseVersion,
    parseCaseVersion,
} from '@/api/caseVersion';
import { acquireCaseMutationSlot } from '@/api/caseMutationQueue';

const ONE_SECOND = 1000;

export const CONFIG = {
    exponentialBackoffMultiplier: ONE_SECOND,
    requestTimeoutLimit: ONE_SECOND * 30,
};

export type UserFriendlyError = string;

export const isUserFriendlyErrorMessage = <T>(
    error: T | UserFriendlyError,
): error is UserFriendlyError => typeof error === 'string';

type RetryableRequestConfig = InternalAxiosRequestConfig & {
    retried?: boolean;
    retryAttempt?: number;
};

const IF_CASE_VERSION = 'If-Case-Version';
const X_CASE_VERSION = 'x-case-version';

function _guardedCaseId(config: InternalAxiosRequestConfig): number | null {
    return caseIdFromMutationUrl(config.url ?? '', config.method ?? 'get', config.baseURL);
}

function _responseCaseVersion(response: AxiosResponse | undefined): number | undefined {
    const header = response?.headers[X_CASE_VERSION];
    return header === undefined || header === null ? undefined : parseCaseVersion(String(header));
}

function _isStaleResponse(response: AxiosResponse | undefined): boolean {
    return response?.status === HttpStatusCode.PreconditionFailed;
}

/** A 412 is not recorded, so the case stays stale until the page reloads. */
async function _sendGuarded(
    send: AxiosAdapter,
    config: InternalAxiosRequestConfig,
    caseId: number,
): Promise<AxiosResponse> {
    const release = await acquireCaseMutationSlot(caseId);
    let response: AxiosResponse | undefined;
    try {
        const version = currentCaseVersion(caseId);
        if (version !== undefined) config.headers.set(IF_CASE_VERSION, String(version));
        response = await send(config);
        return response;
    } catch (error) {
        response = axios.isAxiosError(error) ? error.response : undefined;
        throw error;
    } finally {
        if (!_isStaleResponse(response)) observeCaseVersion(caseId, _responseCaseVersion(response));
        release();
    }
}

/** Adapter-level, so each retry or re-auth re-enters the queue with the latest version. */
function _caseVersionAdapter(send: AxiosAdapter): AxiosAdapter {
    return (config) => {
        const caseId = _guardedCaseId(config);
        return caseId === null ? send(config) : _sendGuarded(send, config, caseId);
    };
}

export const client = axios.create({
    baseURL: apiBaseUrl(),
    timeout: CONFIG.requestTimeoutLimit,
    headers: { 'Content-Type': 'application/json' },
    validateStatus: (status: number) =>
        (status >= 200 && status < 300) || [400, 401, 403, 404].includes(status),
    adapter: _caseVersionAdapter(axios.getAdapter(axios.defaults.adapter)),
});

export function expectedErrorOrRethrow<E>(
    error: E,
    genericUserFriendlyError: UserFriendlyError,
): UserFriendlyError {
    if (error instanceof Object && error.constructor.name === 'AxiosError') {
        // server errors including 500s, 502s, or network errors where
        // the request timed out or the client is offline are expected and can be handled.
        return genericUserFriendlyError;
    } else {
        // this could be a js syntax error or some other serious exception
        throw error;
    }
}

/**
 * Include access token as authorization header to all requests.
 */
client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
        const tokens = await unexpiredAuthTokens();
        if (tokens) {
            const acceptHeader = config.headers['Accept'];
            if (!acceptHeader || acceptHeader === '') {
                config.headers['Accept'] = 'application/json';
            }

            config.headers['Authorization'] = `Bearer ${tokens.access}`;
        }
        return config;
    },
    (error: AxiosError) => Promise.reject(error),
);

/** Registered first, so the interceptors below get a StaleCaseError. */
client.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError | Error) => {
        if (!axios.isAxiosError(error) || !error.config || !_isStaleResponse(error.response)) {
            return Promise.reject(error);
        }
        const caseId = _guardedCaseId(error.config);
        if (caseId === null) return Promise.reject(error);

        const stale = new StaleCaseError(caseId, _responseCaseVersion(error.response));
        notifyCaseStale(stale);
        return Promise.reject(stale);
    },
);

/**
 * Refresh access token when it has expired.
 */
client.interceptors.response.use(
    async (response: AxiosResponse) => {
        // Note: 401 is a valid response code, as it was listed in the client definition
        if (response?.status === HttpStatusCode.Unauthorized) {
            const config: RetryableRequestConfig = response.config;

            /**
             * there is an edge case where a 401 response when refreshing the token
             * can start an endless loop. this prevents that from happening.
             */
            if (config.retried) {
                return Promise.reject('Could not reauthenticate user.');
            }

            const tokens = await cachedAuthTokens();
            if (!tokens) {
                return Promise.reject('Could not reauthenticate user.');
            }

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const [_newTokens, responseCode] = await refreshTokens(tokens.refresh);

            if (responseCode === HttpStatusCode.Ok) {
                /** retry request when new access token is available. */
                config.retried ??= true;
                return client(config);
            } else {
                // If the refresh token was invalid (e.g.: expired already), the user is forced to authenticate again
                const auth = useAuth();
                await auth.logout();
            }
        }
        return response;
    },
    (error: AxiosError) => Promise.reject(error),
);

/** A 412 is never retried: the same request gets the same answer. */
function _retryableConfig(error: AxiosError | Error): RetryableRequestConfig | undefined {
    if (!axios.isAxiosError(error) || _isStaleResponse(error.response)) return undefined;
    return error.config;
}

/**
 * A failed request is sent up to three times in total.
 */
client.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError | Error) => {
        const config = _retryableConfig(error);

        if (!config) {
            return Promise.reject(error);
        }

        config.retryAttempt ??= 1;

        if (config.retryAttempt === 3) {
            return Promise.reject(error);
        }

        config.retryAttempt += 1;
        const increasingBackoff = CONFIG.exponentialBackoffMultiplier * config.retryAttempt;
        const delayedRequest = new Promise<void>((resolve) => {
            setTimeout(() => {
                resolve();
            }, increasingBackoff);
        });
        return delayedRequest.then(() => client(config));
    },
);
